import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Users, Clock, FileSearch, RefreshCw, CheckCircle2, XCircle, Activity, Inbox, ArrowRight } from "lucide-react";
import { useOutletContext, useNavigate } from "react-router-dom";

import { Card } from "@/components/ui/Card";
import { Button, ButtonShadcn } from "@/components/ui/Button";
import Skeleton from "@/components/shared/Skeleton";
import PermissionGuard from "@/permissions/PermissionGuard";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import { MODULES } from "@/permissions/modules";
import { PERMISSIONS } from "@/permissions/constant";
import { cn } from "@/lib/utils";

import { ComplianceService } from "@/services/complianceService";
import { COMPLIANCE_STATUS } from "../constant";
import NeedsAttentionPanel from "./components/NeedsAttentionPanel";
import ActivityRow from "../components/ActivityRow";
import useComplianceLiveUpdates from "../hooks/useComplianceLiveUpdates";
import StatusDonut from "./components/StatusDonut";

// Accent colors per stat (Tailwind border-left + icon tint). Kept feature-local
// so the dashboard doesn't couple to the recruitment dashboard's STAT_META.
// `filterStatus` (Phase 2A, DL-Q5) makes a card a launchpad: clicking it opens
// Document Intake pre-filtered to that status. "total" has no status filter →
// it opens the unfiltered Intake.
const IN_PROCESS_STATUSES = [COMPLIANCE_STATUS.PENDING_FROM_CANDIDATE, COMPLIANCE_STATUS.PENDING_FOR_REVIEW, COMPLIANCE_STATUS.RE_REQUESTED].join(",");

const STAT_CARDS = [
  { key: "total", label: "Candidates in Process", icon: Users, color: "#6366f1", filterStatus: IN_PROCESS_STATUSES },
  { key: "pendingFromCandidate", label: "Pending from Candidate", icon: Clock, color: "#a855f7", filterStatus: COMPLIANCE_STATUS.PENDING_FROM_CANDIDATE },
  { key: "pendingForReview", label: "Pending for Review", icon: FileSearch, color: "#3b82f6", filterStatus: COMPLIANCE_STATUS.PENDING_FOR_REVIEW },
  { key: "reRequested", label: "Re-requested", icon: RefreshCw, color: "#f59e0b", filterStatus: COMPLIANCE_STATUS.RE_REQUESTED },
  { key: "approved", label: "Approved", icon: CheckCircle2, color: "#22c55e", filterStatus: COMPLIANCE_STATUS.APPROVED },
  { key: "rejected", label: "Rejected", icon: XCircle, color: "#ef4444", filterStatus: COMPLIANCE_STATUS.REJECTED },
];

const DAY_MS = 86_400_000;

// Activity feed windows. The feed loads once and is narrowed in memory — the
// backend has no `from` param yet (BACKEND_REQUIREMENTS #8).
const ACTIVITY_RANGES = [
  { key: "Today", label: "Today", days: 1 },
  { key: "7D", label: "7D", days: 7 },
  { key: "30D", label: "30D", days: 30 },
];

// How many attention rows to pull per worklist. The endpoint is candidate-
// grained now — one row per candidate, with the honest candidate `total` beside
// it — so this is exactly the number of rows shown, with no over-fetch to
// absorb client-side grouping.
const ATTENTION_LIMIT = 5;

const ACTIVITY_LIMIT = 50;

// Clickable when `onClick` is supplied (Phase 2A, DL-Q5 — drill into a filtered
// Intake). Renders as a real button so it's keyboard-accessible; falls back to
// a plain div otherwise.
const StatCard = ({ label, value, color, icon: Icon, loading, onClick }) => {
  const clickable = typeof onClick === "function";
  const cardClassName = cn(
    "group relative rounded-2xl p-4 h-full w-full items-stretch flex flex-col justify-between bg-background-secondary border border-border-primary text-left shadow-2xs",
    clickable && "cursor-pointer hover:border-button-primary/40 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all",
  );
  const content = (
    <>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border" style={{ background: `${color}1a`, borderColor: `${color}33` }}>
          <Icon size={16} style={{ color }} />
        </div>
        {loading ? <Skeleton className="h-7 w-12" /> : <p className="text-[1.8rem] font-bold tracking-tight leading-none text-text-primary">{value ?? 0}</p>}
      </div>
      {loading ? <Skeleton className="h-3 w-24 mt-2" /> : <p className="text-[0.8rem] mt-2 leading-tight text-text-secondary">{label}</p>}
    </>
  );

  if (!clickable) {
    return (
      <div className={cardClassName} style={{ borderLeftWidth: 4, borderLeftColor: color }}>
        {content}
      </div>
    );
  }

  return (
    <ButtonShadcn variant="ghost" onClick={onClick} className={cardClassName} style={{ borderLeftWidth: 4, borderLeftColor: color }}>
      {content}
    </ButtonShadcn>
  );
};

// Compliance Dashboard landing (plan §13a / D7 + §13c / D9 + Phase 2A). Layout,
// top→bottom: (1) real stat cards (GET /stats), each a launchpad into a
// pre-filtered Intake; (2) the "Needs Attention" worklist (GET
// /dashboard/attention); (3) Live Activity Feed (left, GET /dashboard/activity)
// beside the Status Distribution donut (right). All built from existing
// primitives.
//
// Connectivity Phase F removed the compliance funnel from this dashboard. It
// counted candidates by stage across the whole tenant, which is a shape of the
// question nobody asked here; the per-candidate version now lives on the
// candidate's own Compliance tab, where the recruiter is already looking.
const ComplianceDashboard = () => {
  const setHeader = useOutletContext();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityRange, setActivityRange] = useState("7D");
  // Phase 2A: "Needs Attention" worklists.
  const [attention, setAttention] = useState(null);
  const [attentionLoading, setAttentionLoading] = useState(true);

  // Drill-through (DL-Q5): open Document Intake, optionally pre-filtered.
  const goToIntake = (status) => navigate(status ? `/admin/compliance/intake?status=${status}` : "/admin/compliance/intake");

  useEffect(() => {
    setHeader({
      title: "Compliance",
      subTitle: "Dashboard",
    });
    return () => setHeader({ title: "", subTitle: "" });
  }, [setHeader]);

  // One fetch routine for both mount and every live update, so a WS-driven
  // refresh can never drift from what the page loads initially.
  // `activeRef` is not used here: each call is self-contained and the last
  // response wins, which is correct for a "current state" dashboard.
  const refresh = useCallback(() => {
    let active = true;
    setLoading(true);
    ComplianceService.getStats()
      .then((data) => {
        if (active) setStats(data);
      })
      .catch(() => {
        if (active) setStats(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    setActivityLoading(true);
    ComplianceService.getActivity(ACTIVITY_LIMIT)
      .then((data) => {
        if (active) setActivity(data?.activity ?? []);
      })
      .catch(() => {
        if (active) setActivity([]);
      })
      .finally(() => {
        if (active) setActivityLoading(false);
      });

    // Phase 2A widgets — independent fetches so a slow/failed one never
    // blocks the stat cards or each other; each degrades to an empty state.
    setAttentionLoading(true);
    ComplianceService.getAttention(ATTENTION_LIMIT)
      .then((data) => {
        if (active) setAttention(data ?? null);
      })
      .catch(() => {
        if (active) setAttention(null);
      })
      .finally(() => {
        if (active) setAttentionLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const cancel = refresh();
    return cancel;
  }, [refresh]);

  // Live updates. The dashboard reads four endpoints, so every key it depends
  // on must be dropped before refetching — `dedupeGet` caches the promise for
  // 2s and would otherwise hand back the pre-event one.
  useComplianceLiveUpdates({
    keys: ["getStats", `getAttention_${ATTENTION_LIMIT}`, `getActivity_${ACTIVITY_LIMIT}_`],
    onRefresh: refresh,
    // No optimistic prepend. The feed row needs the candidate, document and
    // actor *names*, and the event envelope carries only a resource id —
    // rendering "A record approved" for ~300ms before the refetch replaced
    // it with the real row was worse than the small delay. The feed is still
    // live; it is the debounced refetch that fills it.
  });

  // Narrow the already-loaded feed to the selected window. Items with no
  // timestamp are kept — dropping them would hide real events.
  const filteredActivity = useMemo(() => {
    const days = ACTIVITY_RANGES.find((r) => r.key === activityRange)?.days ?? 7;
    const cutoff = Date.now() - days * DAY_MS;
    return activity.filter((item) => !item.at || new Date(item.at).getTime() >= cutoff);
  }, [activity, activityRange]);

  return (
    <PermissionGuard permission={PERMISSIONS.COMPLIANCE_VIEW}>
      <div className="flex flex-col gap-4" style={{ zoom: 0.9 }}>
        {/* The section's landing page, so it is where a tenant learns
                    that Compliance is switched off fleet-wide. Everything on
                    this page is a read and keeps working. */}
        {/* Keep `<ModuleUnavailableNotice module={MODULES.COMPLIANCE}` on one
                    line: scripts/check-module-surfaces.mjs matches that exact string
                    to prove this surface is gated, and it reads line by line. */}
        <ModuleUnavailableNotice module={MODULES.COMPLIANCE} suffix="New uploads and review decisions are paused. Every figure below is up to date." />

        {/* 1. Stat cards — real tenant-scoped counts (GET /stats). Each is
                    a launchpad into a pre-filtered Document Intake (DL-Q5). */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {STAT_CARDS.map((c) => (
            <StatCard key={c.key} label={c.label} value={stats?.[c.key]} color={c.color} icon={c.icon} loading={loading} onClick={() => goToIntake(c.filterStatus)} />
          ))}
        </div>

        {/* 2. "Needs Attention" worklist — the centerpiece. */}
        <NeedsAttentionPanel data={attention} loading={attentionLoading} />

        {/* 3. Live Activity Feed (left) + Status Distribution (right).
                    The donut needs less horizontal room, so the feed takes 2/3
                    and the donut 1/3; they stretch to equal height. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
          <Card className="lg:col-span-2 flex flex-col gap-1 p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-border-primary flex items-center gap-2 flex-wrap">
              <Activity size={14} className="text-text-primary/70" />
              <h3 className="text-sm font-semibold text-text-primary">Live Activity Feed</h3>

              <div className="ml-auto flex items-center gap-1 bg-background-form/60 rounded-full p-0.5 border border-border-primary/60">
                {ACTIVITY_RANGES.map((range) => (
                  <Button
                    key={range.key}
                    type="button"
                    onClick={() => setActivityRange(range.key)}
                    className={cn(
                      "px-3 py-1 rounded-full text-[11px] font-semibold transition-colors",
                      activityRange === range.key ? "bg-button-primary text-text-invert shadow-sm" : "text-text-primary/60 hover:text-text-primary",
                    )}
                  >
                    {range.label}
                  </Button>
                ))}
              </div>

              {/* The feed is a recent digest — capped rows, narrowed in memory.
                  Anyone entitled to the full trail gets a way to it from here,
                  which is where they are when they notice the feed stops short.
                  Everyone else keeps the feed exactly as it is. */}
              <PermissionGuard permissions={[PERMISSIONS.COMPLIANCE_AUDIT_VIEW]} isComponent>
                <Button
                  type="button"
                  onClick={() => navigate("/admin/compliance/audit-log")}
                  className="flex items-center gap-1 text-[11px] font-medium text-button-primary hover:underline"
                >
                  View all
                  <ArrowRight size={12} />
                </Button>
              </PermissionGuard>
            </div>

            <div className="flex-1 min-h-0 max-h-[360px] overflow-y-auto custom-scrollbar px-4 py-1">
              {activityLoading ? (
                <div className="flex flex-col gap-2 py-3">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : filteredActivity.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
                  <div className="w-12 h-12 rounded-2xl bg-background-form flex items-center justify-center">
                    <Inbox size={22} className="text-text-primary/30" />
                  </div>
                  <p className="text-xs font-medium text-text-primary">No activity in this period</p>
                  <p className="text-[11px] text-text-secondary max-w-[260px]">
                    {activity.length > 0 ? "Try a wider range to see older compliance events." : "Compliance events will appear here once candidates start uploading."}
                  </p>
                  {activity.length > 0 && activityRange !== "30D" && (
                    <Button type="button" onClick={() => setActivityRange("30D")} className="mt-1 text-[11px] font-medium text-button-primary hover:underline">
                      Show last 30 days
                    </Button>
                  )}
                </div>
              ) : (
                filteredActivity.map((item) => <ActivityRow key={item.id} item={item} />)
              )}
            </div>
          </Card>

          <StatusDonut stats={stats} loading={loading} />
        </div>

      </div>
    </PermissionGuard>
  );
};

export default ComplianceDashboard;
